import React from 'react';
import Paper from '../Paper/Paper';
import { Pie, PieConfig } from '@ant-design/charts';
import { Skeleton, Typography } from 'antd';
import styles from './DashboardChartWidget.module.less';
import classnames from 'classnames';
import { colorPrimary } from '../../styles/theme';

type chartDataItem = {
  type: string;
  value: number;
  numValue: number | string;
  color: string;
};

interface DashboardChartWidgetProps {
  isLoading: boolean;
  data: chartDataItem[];
  chartTitle: string;
  total: number;
  isError: boolean;
}

const getChartConfig = (data: chartDataItem[], total: number): PieConfig => {
  const colors = data.map(({ color }) => color);
  return {
    appendPadding: 10,
    data: data,
    angleField: 'value',
    height: 158,
    width: 170,
    colorField: 'type',
    color: colors,
    pieStyle: {
      lineWidth: 0,
      cursor: 'pointer'
    },
    radius: 1,
    innerRadius: 0.6,
    label: {
      type: 'inner',
      offset: '-50%',
      content: function content(_ref) {
        const percent = _ref.value;
        return `${percent}%`;
      },
      autoRotate: false,
      style: {
        textAlign: 'center',
        fontSize: 12,
        color: colorPrimary
      }
    },
    tooltip: {
      formatter: item => {
        return { name: item.type, value: `${item.value}%` };
      }
    },
    legend: false,
    interactions: [{ type: 'element-selected' }, { type: 'element-active' }],
    statistic: {
      title: undefined,
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: '24px'
        },
        content: `${total}`
      }
    }
  };
};

const renderLegend = (data: chartDataItem[], isError: boolean) => {
  return data.map(item => (
    <div key={`legend${item.type}${item.value}`}>
      <div>
        <Typography.Text className={classnames([styles.legendTitleText, styles[item.color]])}>
          {item.type}
        </Typography.Text>
      </div>
      <div>
        <Typography.Text className={styles.legendContentText}>{isError ? 'n/a' : item.numValue}</Typography.Text>
      </div>
    </div>
  ));
};

const DashboardChartWidget = ({ isLoading, data, chartTitle, total, isError }: DashboardChartWidgetProps) => {
  const config = getChartConfig(data, total);
  const legend = renderLegend(data, isError);
  return (
    <Paper data-testid="dashboardChartWidget" className={styles.paperWithHeight}>
      {!isLoading ? (
        <div className={styles.wrapper}>
          <div>
            <Typography.Text className={styles.chartTitleText}>{chartTitle}</Typography.Text>
          </div>
          <div className={styles.widgetContainer}>
            {!isError && <Pie {...config} />}
            <div className={styles.legendContainer}>{legend}</div>
            <div />
          </div>
        </div>
      ) : (
        <Skeleton active paragraph={{ rows: 8 }} />
      )}
    </Paper>
  );
};

export default DashboardChartWidget;
